import { useRef, useState } from 'react'
import { useContactStore } from '../../store/contactStore'
import { useContactDetail, useContactReports } from '../../hooks/useContacts'
import { useFlagReport } from '../../hooks/useReports'
import { useAuth } from '../../hooks/useAuth'
import { SPECIES_LABELS, EVENT_TYPE_LABELS, STATUS_LABELS, officialSourceLabel } from '../../constants/taxonomy'
import { STATUS_COLORS } from '../map/confidenceStyle'
import LedgerHistory from './LedgerHistory'
import type { ContactReportSummary, EventType, SpeciesBucket } from '../../types/contact'

function FlagControl({ report }: { report: ContactReportSummary }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [justFlagged, setJustFlagged] = useState(false)
  const mutation = useFlagReport()

  if (justFlagged || report.flag_count > 0) {
    return (
      <span className="text-xs text-amber-700">
        Flagged{report.flag_count > 0 ? ` (${report.flag_count})` : ''}
      </span>
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-gray-500 hover:text-gray-700 font-medium"
      >
        Flag as suspicious
      </button>
    )
  }

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="text"
        placeholder="Why?"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="w-28 rounded border border-gray-300 text-xs px-1.5 py-1"
      />
      <button
        type="button"
        disabled={mutation.isPending || reason.trim().length === 0}
        onClick={() =>
          mutation.mutate(
            { reportId: report.id, reason },
            { onSuccess: () => setJustFlagged(true) },
          )
        }
        className="text-xs text-white bg-amber-600 hover:bg-amber-500 disabled:opacity-50 rounded px-2 py-1 font-medium"
      >
        Confirm
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="text-xs text-gray-500 hover:text-gray-700"
      >
        Cancel
      </button>
    </div>
  )
}

const TIER_LABELS: Record<string, string> = {
  unverified: 'unverified',
  verified_individual: 'verified individual',
  verified_professional: 'verified professional',
  official: 'official source',
}

export default function ContactDetail() {
  const selectedId = useContactStore((s) => s.selectedContactId)
  const selectContact = useContactStore((s) => s.selectContact)
  const { data: contact, isLoading } = useContactDetail(selectedId)
  const { isAuthenticated } = useAuth()
  const [showLedger, setShowLedger] = useState(false)
  const [showReports, setShowReports] = useState(false)
  const reportsSectionRef = useRef<HTMLDivElement>(null)
  const ledgerSectionRef = useRef<HTMLDivElement>(null)
  const { data: contactReports, isLoading: reportsLoading } = useContactReports(selectedId, showReports)

  if (!selectedId) return null

  // Expanding "Ledger history" grows the content below the fold, but on a real phone the
  // scroll container's max scroll position can land just short of fully revealing the new
  // content (address-bar animation, momentum-scroll rounding) — reported by a user as "the
  // verify link is visible but I can't scroll far enough to reach it." Scrolling the section
  // into view on expand removes the need to rely on the user's own swipe reaching the exact
  // bottom edge. `behavior: 'instant'`, not 'smooth' — smooth scrollIntoView silently no-ops
  // inside a nested overflow-y-auto container in this environment (verified: scrollTop stayed
  // 0 for 1.5s with 'smooth', jumped immediately with 'instant'), and WebKit/iOS Safari has a
  // long history of the same flakiness for smooth-scrollIntoView in nested scroll containers.
  // Delayed a tick so it runs after the (usually-instant, often-cached) ledger query resolves
  // and the section has grown to its final height.
  function toggleLedger() {
    setShowLedger((v) => {
      const next = !v
      if (next) {
        setTimeout(() => ledgerSectionRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' }), 150)
      }
      return next
    })
  }

  // Same fix as toggleLedger above, and for the same reason: expanding this section grows
  // content below the fold, and on a real phone the scroll container can land short of it
  // (address-bar animation, momentum-scroll rounding), making it look like the tap did
  // nothing even though the reports loaded.
  function toggleReports() {
    setShowReports((v) => {
      const next = !v
      if (next) {
        setTimeout(() => reportsSectionRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' }), 150)
      }
      return next
    })
  }

  return (
    // key resets showLedger (and any other local state) when the selected contact changes,
    // rather than carrying "history expanded" over onto an unrelated contact.
    <div key={selectedId} className="h-full overflow-y-auto overscroll-contain p-4 pb-16">
      <button
        type="button"
        onClick={() => selectContact(null)}
        className="text-xs text-gray-500 hover:text-gray-700 mb-3"
      >
        ← Back to list
      </button>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      {contact && (
        <div className="space-y-4">
          <div>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              style={{
                color: STATUS_COLORS[contact.status],
                backgroundColor: STATUS_COLORS[contact.status] + '1a',
              }}
            >
              {STATUS_LABELS[contact.status]}
            </span>
            <h2 className="text-lg font-semibold text-gray-900 mt-2">
              {SPECIES_LABELS[contact.species_bucket]}
            </h2>
            <p className="text-xs text-gray-500">
              {contact.lat.toFixed(4)}, {contact.lon.toFixed(4)} · uncertainty ≈{' '}
              {Math.round(contact.uncertainty_radius_m / 100) / 10} km
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 p-3 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Reports</span>
              <span className="text-gray-900 font-medium">{contact.report_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Confidence score</span>
              <span className="text-gray-900 font-medium">{contact.confidence_score.toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">First observed</span>
              <span className="text-gray-900 font-medium">
                {new Date(contact.first_observed_at).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Last updated</span>
              <span className="text-gray-900 font-medium">
                {new Date(contact.last_updated_at).toLocaleString()}
              </span>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 mb-1.5">
              Reporter mix (aggregate only — no individual identities shown)
            </p>
            <ul className="text-sm text-gray-800 space-y-0.5">
              {Object.entries(contact.reporter_tier_mix).map(([tier, count]) => (
                <li key={tier}>
                  {count} × {TIER_LABELS[tier] ?? tier}
                </li>
              ))}
            </ul>
          </div>

          {contact.is_declared && (
            <div className="rounded-lg bg-green-50 border border-green-300 text-green-800 px-3 py-2 text-sm">
              Linked to an official detection
              {contact.linked_official_alert_id && ` (${contact.linked_official_alert_id})`}.
            </div>
          )}

          {!contact.is_declared && (
            <p className="text-xs text-gray-400">
              This contact has not been officially confirmed. Treat it as situational awareness,
              not a substitute for reporting to your state biosecurity agency or the Emergency
              Animal Disease Watch Hotline (1800 675 888).
            </p>
          )}

          <div ref={reportsSectionRef} className="border-t border-gray-200 pt-3">
            {isAuthenticated ? (
              <button
                type="button"
                onClick={toggleReports}
                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
              >
                {showReports ? '▾' : '▸'} Individual reports ({contact.report_count})
              </button>
            ) : (
              <p className="text-xs text-gray-400">
                <a href="/login" className="text-blue-600 hover:text-blue-700 font-medium">Sign in</a>{' '}
                to view and flag this contact's individual reports.
              </p>
            )}
            {showReports && (
              <div className="mt-2 space-y-2">
                {reportsLoading && <p className="text-xs text-gray-500">Loading reports…</p>}
                {contactReports?.map((r) => (
                  <div key={r.id} className="rounded-lg border border-gray-200 p-2.5 text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="text-xs font-medium text-gray-900">
                          {SPECIES_LABELS[r.species_bucket as SpeciesBucket] ?? r.species_bucket}
                        </span>
                        <span className="text-xs text-gray-500 ml-1.5">
                          {EVENT_TYPE_LABELS[r.event_type as EventType] ?? r.event_type}
                        </span>
                        {r.description && (
                          <p className="text-xs text-gray-700 mt-0.5">{r.description}</p>
                        )}
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {new Date(r.observed_at).toLocaleString()} · {r.reporter_tier}
                          {officialSourceLabel(r.source_agency) && ` · Source: ${officialSourceLabel(r.source_agency)}`}
                        </p>
                      </div>
                      <div className="shrink-0">
                        <FlagControl report={r} />
                      </div>
                    </div>
                  </div>
                ))}
                {contactReports && contactReports.length === 0 && (
                  <p className="text-xs text-gray-500">No individual reports to show.</p>
                )}
              </div>
            )}
          </div>

          <div ref={ledgerSectionRef} className="border-t border-gray-200 pt-3">
            <button
              type="button"
              onClick={toggleLedger}
              className="text-xs text-gray-500 hover:text-gray-700 font-medium"
            >
              {showLedger ? '▾' : '▸'} Ledger history
            </button>
            {showLedger && (
              <div className="mt-2">
                <LedgerHistory contactId={contact.id} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
